import React, { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import ExporterSearchDropdown from '../../common/ExporterSearchDropdown';
import { getSupplier } from '../../../services/supplierService';

// Helper to extract Beneficiary Address from supplier bank details or direct properties
const extractBeneficiaryAddress = (s) => {
  if (!s) return '';
  if (Array.isArray(s.bankDetails) && s.bankDetails.length > 0) {
    const primaryBank = s.bankDetails.find((b) => b?.isPrimary) || s.bankDetails[0];
    const addr = primaryBank?.accountHolderAddress || primaryBank?.beneficiaryAddress || primaryBank?.bankAddress;
    if (addr && addr.trim()) return addr.trim();
  }
  return s.accountHolderAddress || s.beneficiaryAddress || s.address || '';
};

/**
 * InvDetailsTab — INVOICE DETAILS tab
 * standard Bootstrap 5 horizontal form layout
 * Uniform 5:7 grid split ensures all input fields align perfectly at the same horizontal level.
 */
const InvDetailsTab = ({ control, setValue, errors }) => {
  // Auto-read totals from form store (set by SummaryTab)
  const invoiceQuantity = useWatch({ control, name: 'invoiceQuantity' });
  const invoiceRate = useWatch({ control, name: 'invoiceRate' });
  const roundOffInvAmount = useWatch({ control, name: 'roundOffInvAmount' });

  // INVOICE AMOUNT = (invoiceQuantity × invoiceRate) + roundOffInvAmount (matches legacy software)
  const calculatedInvoiceAmount = React.useMemo(() => {
    const q = Number(invoiceQuantity) || 0;
    const r = Number(invoiceRate) || 0;
    const ro = Number(roundOffInvAmount) || 0;
    const v = (q * r) + ro;
    return (q || r || ro) ? v.toFixed(2) : '0.00';
  }, [invoiceQuantity, invoiceRate, roundOffInvAmount]);

  useEffect(() => {
    if (setValue) {
      setValue('bgAmtInterestAmt', calculatedInvoiceAmount);
    }
  }, [calculatedInvoiceAmount, setValue]);
  return (
    <div className="w-100">
      <h5 className="de-section-title">INVOICE DETAILS</h5>

      <div className="row g-3">
        {/* Row 1: EXPORTER (1/3 width) & ADDRESS (2/3 width) */}
        <div className="col-12 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">EXPORTER :</label>
            <div className="col-7">
              <Controller
                name="exporter"
                control={control}
                render={({ field }) => (
                  <ExporterSearchDropdown
                    id="exporter-search"
                    value={field.value || ''}
                    onChange={async (val, selectedSupplier) => {
                      field.onChange(val);

                      // If exporter is cleared, also clear the address field
                      if (!val) {
                        if (setValue) setValue('address', '');
                        return;
                      }

                      // If a supplier object was selected, extract beneficiary address
                      if (selectedSupplier) {
                        let addr = extractBeneficiaryAddress(selectedSupplier);
                        const suppId = selectedSupplier.id || selectedSupplier.supplierId;
                        if (!addr && suppId) {
                          try {
                            const fullSupplier = await getSupplier(suppId);
                            addr = extractBeneficiaryAddress(fullSupplier);
                          } catch (err) {
                            // ignore fetch errors silently
                          }
                        }
                        if (setValue) {
                          setValue('address', addr || '');
                        }
                      } else {
                        // Custom typed exporter name without supplier record
                        if (setValue) {
                          setValue('address', '');
                        }
                      }
                    }}
                    onBlur={field.onBlur}
                    placeholder="Exporter"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="row align-items-center g-2">
            <label className="col-form-label de-label text-start de-label-span-2">ADDRESS :</label>
            <div className="de-input-span-2">
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="Address" />
                )}
              />
            </div>
          </div>
        </div>

        {/* Row 2: INVOICE NO, INVOICE DT, INVOICE QUANTITY */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">INVOICE NO :</label>
            <div className="col-7">
              <Controller
                name="invoiceno"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="Invoice No" />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">INVOICE DT :</label>
            <div className="col-7">
              <Controller
                name="invoicedt"
                control={control}
                render={({ field }) => (
                  <input
                    type="date"
                    className="form-control"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* INVOICE QUANTITY — auto-filled from totalNetCbm (Summary) */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">INVOICE QUANTITY :</label>
            <div className="col-7">
              <Controller
                name="invoiceQuantity"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    className="form-control"
                    placeholder="Quantity"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Row 3: INVOICE RATE, COST USD RATE, INVOICE AMOUNT */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">INVOICE RATE :</label>
            <div className="col-7">
              <Controller
                name="invoiceRate"
                control={control}
                render={({ field }) => (
                  <input {...field} type="number" className="form-control" placeholder="Invoice Rate" />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-start g-2">
            <div className="col-5 d-flex flex-column align-items-start">
              <label className="col-form-label de-label text-start p-0 mb-1">COST USD RATE :</label>
              <Controller
                name="roundOffInvAmount"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    step="any"
                    className="form-control form-control-sm bg-white"
                    placeholder="Round Off"
                    title="Round Off Amount (Adds to Invoice Amount)"
                  />
                )}
              />
            </div>
            <div className="col-7">
              <Controller
                name="sblcInterestUsdRate"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    step="any"
                    className="form-control"
                    placeholder="Cost USD Rate"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* INVOICE AMOUNT — computed: (invoiceQuantity × invoiceRate) + roundOffInvAmt, read-only */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">INVOICE AMOUNT :</label>
            <div className="col-7">
              <Controller
                name="bgAmtInterestAmt"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    readOnly
                    className="form-control font-monospace fw-bold bg-light"
                    placeholder="Invoice Amount"
                    value={calculatedInvoiceAmount}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Row 4: COUNTRY OF ORIGIN, TERMS, COUNTRY FROM GOODS */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-start g-2">
            <label className="col-5 col-form-label de-label text-start" style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>COUNTRY OF ORIGIN :</label>
            <div className="col-7">
              <Controller
                name="countryOfOrigin"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="Country of Origin" />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start" style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>TERMS :</label>
            <div className="col-7">
              <Controller
                name="terms"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="Terms" />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-start g-2">
            <label className="col-5 col-form-label de-label text-start" style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>COUNTRY FROM GOODS :</label>
            <div className="col-7">
              <Controller
                name="countryFromGoods"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="Country From Goods" />
                )}
              />
            </div>
          </div>
        </div>

        {/* Row 5: PROFORMA INVOICE NO, PROFORMA INVOICE DT */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start" style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>PROFORMA INVOICE NO :</label>
            <div className="col-7">
              <Controller
                name="proformaInvoiceNo"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="Proforma Invoice No" />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start" style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>PROFORMA INVOICE DT :</label>
            <div className="col-7">
              <Controller
                name="proformaInvoiceDate"
                control={control}
                render={({ field }) => (
                  <input
                    type="date"
                    className="form-control"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvDetailsTab;
